import { Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { OptionCard, OptionCardContent } from '../../../../../components/EnrolmentManagement/OptionCard';
import { Loader } from '../../../../../components/Loader/Loader';
import { getTimezone } from '../../../../../selectors/hubSelector';
import { TutorTimeSlot } from '../../../../../types/hubInterfaces';
import { DATE_FORMAT_TYPE_7, SESSION_TIME_FORMAT } from '../../../../../util/constants';
import { Checkbox } from '../../../../../v2/components/ui/checkbox';

interface Props {
  timeSlotCallback(param: TutorTimeSlot): void;
  selectedDate: string;
  timeSlots: Array<TutorTimeSlot>;
  tutorName: string;
  tutorSfId: string;
  showOtherTutors: boolean;
  setShowOtherTutors: (showOtherTutors: boolean) => void;
  showHeaderSection: boolean;
  isTutorTimeSlotsLoading: boolean;
}

const TimeSlots = ({
  selectedDate,
  timeSlotCallback,
  timeSlots,
  tutorName,
  tutorSfId,
  showOtherTutors,
  setShowOtherTutors,
  showHeaderSection,
  isTutorTimeSlotsLoading,
}: Props) => {
  const timeZone: string = useSelector(getTimezone);
  const noAvailableSessionTimeSlots = (
    <>
      <p className="m-auto mb-2 text-center text-lg font-bold">No session times available</p>
      <p className="m-auto text-center text-base">Try a different date to see other options</p>
    </>
  );

  return (
    <section className="bg-orange-100">
      {isEmpty(timeSlots) && !showHeaderSection ? (
        noAvailableSessionTimeSlots
      ) : (
        <>
          <h3 className="text-lg font-bold">Select session time</h3>
          <p className="mt-3 text-lg text-grey-5">
            The following times are available on {moment(selectedDate).format(DATE_FORMAT_TYPE_7)}
          </p>
          <div className="my-5 flex items-center gap-x-3">
            <div className="shrink-0">
              <Checkbox
                checked={!showOtherTutors}
                id="showOtherTutors"
                onCheckedChange={() => {
                  setShowOtherTutors(!showOtherTutors);
                }}
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <label className="cursor-pointer text-base" htmlFor="showOtherTutors">
                {`${tutorName}'s availability only`}
              </label>
            </div>
          </div>
          <div className="mt-4">
            <Pending loading={isTutorTimeSlotsLoading} loader={<Loader />}>
              {!isEmpty(timeSlots) ? (
                <>
                  {timeSlots.map((timeSlot) => (
                    <OptionCard onClickHandler={() => timeSlotCallback(timeSlot)} key={timeSlot.start}>
                      <OptionCardContent>
                        <div className="flex w-full flex-row items-center justify-between gap-x-4">
                          <span className="font-semibold md:text-lg">
                            {moment.utc(timeSlot.start).tz(timeZone).format(SESSION_TIME_FORMAT)}
                          </span>
                          <span
                            className={classNames(
                              'text-right text-lg font-normal text-grey-400',
                              tutorSfId === timeSlot.tutorsAvailable[0].tutorId && '!font-semibold'
                            )}
                          >
                            {tutorSfId === timeSlot.tutorsAvailable[0].tutorId
                              ? tutorName
                              : timeSlot.tutorsAvailable[0].tutorName}{' '}
                            available
                          </span>
                        </div>
                      </OptionCardContent>
                    </OptionCard>
                  ))}
                </>
              ) : (
                noAvailableSessionTimeSlots
              )}
            </Pending>
          </div>
        </>
      )}
    </section>
  );
};

export { TimeSlots };
